import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import {
  duration,
  prerequisites,
  genericDiscipline,
} from "../../utilis/constants";
import { get_ApiManager, post_ApiManager } from "../ApiManager/ApiManager";
import swal from "sweetalert";
import { getIntakes, levelsByCategory } from "../Services/apiServices";
import { getCourseFormIntakeOptions } from "./courseIntakeOptions";
import CourseFormSkeleton from "./CourseFormSkeleton";
import { getProviderFormLayout } from "./providerFormLayout";
import ProviderFormMultiselect from "./ProviderFormMultiselect";
import "../../styles/tailwind.css";

const EditCourse = ({
  embedded = false,
  formId,
  courseId: courseIdProp,
  providerId: providerIdProp,
  providerName: providerNameProp,
  category: categoryProp,
  onSuccess,
  onCancel,
  onSavingChange,
}) => {
  const titleRef = useRef();
  const history = useNavigate();
  const {
    providerid: routeProviderId,
    courseid: routeCourseId,
    providername: routeProviderName,
    category: routeCategory,
  } = useParams();
  const providerid = providerIdProp || routeProviderId;
  const courseid = courseIdProp || routeCourseId;
  const providername = providerNameProp || routeProviderName;
  const category = categoryProp || routeCategory;
  const isMigration = category === "Migration";
  const entityLabel = isMigration ? "Application" : "Course";
  const [courseDetail, setCourseDetail] = useState(null);
  const [detailLoaded, setDetailLoaded] = useState(!courseid);
  const [intakesDetail, setIntakesDetail] = useState([]);
  const [selectedOption, setSelectedOption] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState("");
  const [selectedPrerequisites, setSelectedPrerequisites] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [levels, setLevels] = useState([]);
  const [name, setName] = useState("");
  const [prerequisit, setPrerequist] = useState([]);
  const [level, setLevel] = useState("");
  const [fee, setFee] = useState("");
  const [professionalCost, setProfessionalCost] = useState("");
  const [departmentFee, setDepartmentFee] = useState("");
  const [professionalCostError, setProfessionalCostError] = useState("");
  const [departmentFeeError, setDepartmentFeeError] = useState("");

  useEffect(() => {
    if (!embedded) {
      document.title = `Edit ${entityLabel}`;
    }
    if (courseid) {
      getCourseDetail();
    }
    getAllIntakes();
    getLevels();
  }, [embedded, entityLabel, courseid]);

  const getCourseDetail = () => {
    setDetailLoaded(false);
    get_ApiManager(
      courseid,
      "courses",
      (res) => {
        const course = res.data.data["course"];
        setCourseDetail(course);
        setProfessionalCost(course["professional_cost"] ?? "");
        setDepartmentFee(course["department_fee"] ?? "");
        setSelectedDuration(course["duration"] || "");
        const options = res.data.data["intakes"].map((intake) => ({
          value: intake.value,
          label: intake.label,
        }));
        setIntakesDetail(options);
        setSelectedOption(options);
        setSelectedPrerequisites(res.data.data["prerequisite"] || []);
        setSelectedDiscipline(res.data.data["discipline"] || []);
        setPrerequist(res.data.data["prerequisite"] || []);
        if (course?.level_id) {
          setLevel(String(course.level_id));
        }
        setDetailLoaded(true);
      },
      (err) => {
        console.log(err);
        setDetailLoaded(true);
      },
    );
  };

  const getAllIntakes = useCallback(async () => {
    const result = await getIntakes();
    if (result.data) {
      setIntakes(getCourseFormIntakeOptions(result.data.data));
    }
  }, []);

  const getLevels = useCallback(async () => {
    const result = await levelsByCategory();
    if (result.data) {
      setLevels(result.data.data || []);
    }
  }, []);

  const handleCancel = () => {
    if (embedded && onCancel) {
      onCancel();
      return;
    }
    history(`/providers/provider-details/${providerid}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setProfessionalCostError("");
    setDepartmentFeeError("");

    if (professionalCost === "" || professionalCost === null) {
      setProfessionalCostError("Please enter professional cost");
      return false;
    }

    if (isMigration && (departmentFee === "" || departmentFee === null)) {
      setDepartmentFeeError("Please enter department fee");
      return false;
    }

    const data = {
      providerid: courseDetail?.["providerid"] || providerid,
      coursename: name || courseDetail?.["name"],
      prerequisites: prerequisit,
      discipline: selectedDiscipline,
      duration: selectedDuration || courseDetail?.["duration"],
      tution_fee: fee || courseDetail?.["tution_fee"],
      professional_cost: professionalCost,
      department_fee: departmentFee,
      level: level || courseDetail?.["level_id"],
      intakes: selectedOption.length ? selectedOption : intakesDetail,
    };

    onSavingChange?.(true);
    post_ApiManager(
      data,
      `updateCourse/${courseid}`,
      (res) => {
        onSavingChange?.(false);
        if (res.data.error) {
          swal(" ", res.data.error, "error");
        } else {
          swal("Success!", res.data.message, "success");
          if (embedded && onSuccess) {
            onSuccess();
          } else {
            history(`/providers/provider-details/${providerid}`);
          }
        }
      },
      (err) => {
        onSavingChange?.(false);
        console.log(err);
      },
    );
  };

  const ui = getProviderFormLayout(embedded);
  const disabledInputClass = embedded
    ? `${ui.input} cursor-not-allowed bg-gray-50 text-gray-500`
    : `${ui.input} cms-provider-add-input--disabled`;
  const hintClass = embedded
    ? "mt-1 text-xs text-gray-400"
    : "cms-provider-add-hint";

  const formContent = !detailLoaded ? (
    <CourseFormSkeleton embedded={embedded} />
  ) : (
    <form
      id={formId}
      ref={titleRef}
      className={ui.form}
      onSubmit={onSubmit}
    >
      <div className={ui.section}>
        <div
          className={ui.gridClass}
          {...(ui.gridStyle ? { style: ui.gridStyle } : {})}
        >
          <div className={ui.field}>
            <label className={ui.label}>Provider Name</label>
            <div className={ui.control}>
              <input
                type="text"
                disabled
                defaultValue={providername}
                className={disabledInputClass}
              />
            </div>
          </div>

          <div className={ui.field}>
            <label className={ui.label}>
              {entityLabel} Name <span className={ui.requiredMark}>*</span>
            </label>
            <div className={ui.control}>
              <input
                type="text"
                name="name"
                defaultValue={courseDetail?.["name"]}
                onChange={(e) => setName(e.target.value)}
                className={ui.input}
                placeholder="Please Enter"
              />
            </div>
          </div>

          <div className={ui.field}>
            <label className={ui.label}>
              Professional Cost <span className={ui.requiredMark}>*</span>
            </label>
            <div className={ui.control}>
              <input
                type="number"
                className={ui.input}
                placeholder="Please Enter"
                value={professionalCost}
                onChange={(e) => setProfessionalCost(e.target.value)}
              />
            </div>
            {professionalCostError && (
              <small className={ui.error}>{professionalCostError}</small>
            )}
          </div>

          {isMigration && (
            <div className={ui.field}>
              <label className={ui.label}>
                Department Fee <span className={ui.requiredMark}>*</span>
              </label>
              <div className={ui.control}>
                <input
                  type="number"
                  className={ui.input}
                  placeholder="Please Enter"
                  value={departmentFee}
                  onChange={(e) => setDepartmentFee(e.target.value)}
                />
              </div>
              {departmentFeeError && (
                <small className={ui.error}>{departmentFeeError}</small>
              )}
            </div>
          )}

          {!isMigration && (
            <>
              <div className={ui.field}>
                <label className={ui.label}>
                  Level <span className={ui.requiredMark}>*</span>
                </label>
                <div className={ui.control}>
                  <select
                    onChange={(e) => setLevel(e.target.value)}
                    value={level || ""}
                    name="level"
                    className={ui.select}
                  >
                    <option value="">Please Select</option>
                    {levels.map((lvl) => (
                      <option key={lvl.id} value={String(lvl.id)}>
                        {lvl.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={ui.field}>
                <label className={ui.label}>InTakes</label>
                <div className={ui.controlMulti}>
                  <ProviderFormMultiselect
                    options={intakes}
                    placeholder="Intakes"
                    displayValue="label"
                    selectedValues={selectedOption}
                    onSelect={setSelectedOption}
                    onRemove={setSelectedOption}
                    className={embedded ? "w-full" : ""}
                  />
                </div>
              </div>

              <div className={ui.field}>
                <label className={ui.label}>Prerequisites</label>
                <div className={ui.controlMulti}>
                  <ProviderFormMultiselect
                    options={prerequisites}
                    groupBy="group"
                    placeholder="Prerequisites"
                    displayValue="title"
                    selectedValues={selectedPrerequisites}
                    onSelect={(list) => {
                      setPrerequist(list);
                      setSelectedPrerequisites(list);
                    }}
                    onRemove={(list) => {
                      setPrerequist(list);
                      setSelectedPrerequisites(list);
                    }}
                    className={embedded ? "w-full" : ""}
                  />
                </div>
              </div>

              <div className={ui.field}>
                <label className={ui.label}>Discipline</label>
                <div className={ui.controlMulti}>
                  <ProviderFormMultiselect
                    options={genericDiscipline}
                    groupBy="group"
                    placeholder="Discipline"
                    displayValue="title"
                    selectedValues={selectedDiscipline}
                    onSelect={setSelectedDiscipline}
                    onRemove={setSelectedDiscipline}
                    className={embedded ? "w-full" : ""}
                  />
                </div>
              </div>

              <div className={ui.field}>
                <label className={ui.label}>Tution Fee</label>
                <div className={ui.control}>
                  <input
                    type="number"
                    defaultValue={courseDetail?.["tution_fee"]}
                    className={ui.input}
                    placeholder="Please Enter"
                    onChange={(e) => setFee(e.target.value)}
                  />
                </div>
                <p className={hintClass}>According to country&apos;s currency</p>
              </div>

              <div className={ui.field}>
                <label className={ui.label}>Duration</label>
                <div className={ui.control}>
                  <select
                    onChange={(e) => setSelectedDuration(e.target.value)}
                    value={selectedDuration || ""}
                    name="duration"
                    className={ui.select}
                  >
                    <option value="">Please Select</option>
                    {duration.map((value, index) => (
                      <option key={index} value={value.title}>
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {!embedded && (
        <div className={ui.footer}>
          <button
            type="button"
            onClick={handleCancel}
            className={ui.cancelBtn}
          >
            Cancel
          </button>
          <button type="submit" className={ui.submitBtn}>
            Update {entityLabel}
          </button>
        </div>
      )}
    </form>
  );

  if (embedded) {
    return <div className={ui.wrap}>{formContent}</div>;
  }

  return (
    <div className={ui.wrap}>
      <button type="button" onClick={handleCancel} className={ui.backBtn}>
        <FaChevronLeft size={10} />
        Back to Provider
      </button>

      <div className={ui.inner}>
        <div className={ui.cardHeader}>
          <h1 className={ui.cardTitle}>Edit {entityLabel}</h1>
        </div>
        {formContent}
      </div>
    </div>
  );
};

export default EditCourse;
